// ═══════════════════════════════════════════════════════════════
// LAUNCH GATES — what this build is allowed to offer
// ═══════════════════════════════════════════════════════════════
//
// The bridge round-trips native SOL and nothing else. SPL deposits lock
// tokens in the Solana vault, and the withdrawal path that would send them
// back does not exist on the program yet, so an SPL deposit accepted today is
// a deposit nobody can return. The API already refuses those with a 503. This
// file is what stops the UI from inviting them in the first place.
//
// Nothing here deletes a token. The SPL code paths, the metadata, the mint
// pages are all still in the tree; these two switches decide whether a build
// exposes them.
//
// ── VITE_ENABLED_TOKENS ────────────────────────────────────────
//
// A comma-separated allowlist of symbols. Vite inlines it at BUILD time, so
// it belongs in the build environment (a Railway/Docker build arg), not on
// the running container.
//
//   unset / empty     SOL only. This is the launch default and the value a
//                     misconfigured build falls back to.
//   "SOL"             same thing, said out loud.
//   "SOL,USDC,USDT"   SOL plus those two, and only if the bridge contract
//                     also has them registered and enabled.
//   "*"               no local restriction: whatever GET /api/v1/tokens
//                     reports is what the UI offers.
//
// The allowlist is a ceiling, never a floor. A symbol listed here still has
// to come back enabled from the bridge contract before it becomes
// selectable. Adding "USDC" to this variable does not make USDC bridgeable;
// it stops the UI from hiding a USDC that the contract has already enabled.
//
// To reopen SPL tokens once the program can return them: register and enable
// the mint on the DCC bridge contract, then rebuild with the symbol added
// here (or with "*" to defer entirely to the contract).
//
// ── VITE_ENABLE_MINT_PAGES ─────────────────────────────────────
//
// The DUSD and CR-Stable mint/redeem pages. Their API endpoints return 410
// Gone; the design was abandoned. Off unless this is "true" or "1".

/** Symbol every build offers, and the only asset the bridge can return. */
export const NATIVE_SOL_SYMBOL = 'SOL';

/** Symbol every build falls back to when configuration is missing or wrong. */
const FALLBACK_ALLOWLIST = [NATIVE_SOL_SYMBOL];

/** `'all'` defers the decision entirely to the bridge contract. */
export type TokenAllowlist = readonly string[] | 'all';

function parseAllowlist(raw: string | undefined): TokenAllowlist {
  const value = (raw ?? '').trim();
  if (!value) return FALLBACK_ALLOWLIST;
  if (value === '*' || value.toLowerCase() === 'all') return 'all';

  const symbols = value
    .split(',')
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean);

  // An allowlist that parsed to nothing is a typo, not an instruction to ship
  // an empty token picker. Fail toward SOL.
  return symbols.length > 0 ? symbols : FALLBACK_ALLOWLIST;
}

function parseFlag(raw: string | undefined): boolean {
  const value = (raw ?? '').trim().toLowerCase();
  return value === 'true' || value === '1' || value === 'yes';
}

/** Symbols this build may offer, before the bridge contract gets a say. */
export const TOKEN_ALLOWLIST: TokenAllowlist = parseAllowlist(
  import.meta.env.VITE_ENABLED_TOKENS,
);

/** True when this build offers nothing but native SOL. */
export const IS_SOL_ONLY_LAUNCH =
  TOKEN_ALLOWLIST !== 'all' &&
  TOKEN_ALLOWLIST.length === 1 &&
  TOKEN_ALLOWLIST[0] === NATIVE_SOL_SYMBOL;

/** Whether the DUSD / CR-Stable mint pages are reachable in this build. */
export const MINT_PAGES_ENABLED = parseFlag(import.meta.env.VITE_ENABLE_MINT_PAGES);

/** Whether this build is permitted to offer `symbol` at all. */
export function isSymbolAllowed(symbol: string): boolean {
  if (TOKEN_ALLOWLIST === 'all') return true;
  return TOKEN_ALLOWLIST.includes(symbol.toUpperCase());
}
